import './SessionList.css'

import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'

import { User } from '@/services/repository/users'
import { Translates } from '@/services/repository/i18n'
import { SessionsResponse, SessionStat } from '@/pages/communal/statistics/components/common/data'
import { formatDate, secondsToDHM } from '@/common/time'
import { TimeDHM } from '@/components/Elements/time'
import { useModal } from '@/components/modal/Modal'
import { SessionPopBody } from './SessionPop'
import { PathResponse, UserDeviceHistoriesData } from '@/pages/communal/statistics/requests/histories'
import { HitText } from '@/components/hit/HitText'
import { Machine } from '@/services/model/machine'
import { CompareFunction, Order, OrderManager } from '@/components/order/Order'


export const SessionListOrderCols: string[] = ['startAt', 'activeTime', 'onFireTime', 'totalHits', 'maxHit', 'maxSpeed', 'machineId', 'userId']

export const SessionDefaultOrder: Order = {
  key: 'startAt',
  asc: true
}

export const SessionComparableFns: Record<string, CompareFunction<SessionStat>> = {
  'maxHit': (a: SessionStat, b: SessionStat, asc: boolean): number => {
    const aHit: number = Math.max(Number(a.maxHitX), Number(a.maxHitY), Number(a.maxHitZ)) || 0
    const bHit: number = Math.max(Number(b.maxHitX), Number(b.maxHitY), Number(b.maxHitZ)) || 0
    return (aHit - bHit) * (asc ? 1 : -1)
  }
}

export const SessionList = ({ services, session, orderManager, pagginationState, setPagginationState, machine, user, data, historiesData, goToMachinePage, goToUserPage }: {
  services: Services
  session: CurrentSession
  pagginationState: StaticPaginatedTableState
  setPagginationState: Dispatch<SetStateAction<StaticPaginatedTableState>>
  data: SessionsResponse | undefined
  user?: User | undefined
  machine?: Machine | undefined
  historiesData: UserDeviceHistoriesData | undefined | null
  goToMachinePage?: (machineId: string) => void
  goToUserPage?: (userId: string) => void
  orderManager: OrderManager
}) => {
  const translates = session.translates
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [ordered, setOrdered] = useState<SessionStat[]>()

  useEffect(() => {
    let ordered = data ? orderManager.orderListByKey(data || []) : data
    setOrdered(ordered)
  }, [data, orderManager])


  function headerRow(key: string, transKey?: string): React.ReactElement {
    transKey = transKey ? transKey : key
    return <div className='hcol' onClick={() => orderManager.click(key)}>
      {session.translates.for('elements.table.sessions.' + transKey)}
      {orderManager.orderInfoElement(key)}
    </div>
  }

  if (!machine && !user) {
    throw new Error("Machine and user are both missing");
  }

  if (ordered === undefined) {
    return <div className='spinner'></div>
  }

  return <div className='communal-userstatistic-session-list'>
    <StaticPaginatedTable<SessionStat>
      data={ordered}
      parentState={pagginationState}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('startAt', 'start_at')}
        {headerRow('activeTime', 'active_time')}
        {headerRow('onFireTime', 'on_fire_time')}
        {headerRow('totalHits', 'total_hits')}
        {headerRow('maxHit', 'max_hit')}
        {headerRow('maxSpeed', 'max_speed')}
        {!machine ? headerRow('machineId', 'machine_npath') : null}
        {!user ? headerRow('userId', 'user') : null}

        {/* <div className='hcol'>{translates.for('elements.table.sessions.start_at')}</div>
        <div className='hcol'>{translates.for('elements.table.sessions.active_time')}</div>
        <div className='hcol'>{translates.for('elements.table.sessions.on_fire_time')}</div>
        <div className='hcol'>{translates.for('elements.table.sessions.total_hits')}</div>
        <div className='hcol'>{translates.for('elements.table.sessions.max_hit')}</div>
        <div className='hcol'>{translates.for('elements.table.sessions.max_speed')}</div>
        {!machine ? <div className='hcol'>{translates.for('elements.table.sessions.machine_npath')}</div> : null}
        {!user ? <div className='hcol'>{translates.for('elements.table.sessions.user')}</div> : null} */}
      </>}
      row={(row) => {
        const openDetails = () => {
          if (!historiesData) {
            return
          }
          openModal((props) => SessionPopBody({ ...props, session, services, row, user, machine, historiesData }))
        }
        const maxHit: number = Math.max(Number(row.maxHitX), Number(row.maxHitY), Number(row.maxHitZ))
        return <>
          <div className={`col`} onClick={openDetails}>{row.startAt ? formatDate(new Date(row.startAt)) : '-'}</div>
          {/* <div className={`col`} onClick={openDetails}>{ row.endAt ? formatDate(new Date(row.endAt)) : '-'}</div> */}
          <div className={`col`} onClick={openDetails}><TimeDHM dhm={secondsToDHM(row.activeTime)} translates={translates} /></div>
          <div className={`col`} onClick={openDetails}><TimeDHM dhm={secondsToDHM(row.onFireTime)} translates={translates} /></div>
          <div className={`col`} onClick={openDetails}>{row.totalHits ? row.totalHits : '0'}</div>
          <div className={`col`} onClick={openDetails}><HitText value={maxHit}>{maxHit.toFixed(2)}</HitText></div>
          <div className={`col`} onClick={openDetails}>{row.maxSpeed ? row.maxSpeed + ' km/h' : '-'}</div>
          {!machine ? <div className={`col`} onClick={() => goToMachinePage ? goToMachinePage(row.machineId) : navigate(`/communal/statistics/machines?machine=${row.machineId}`)}>
            {[
              row.manufacturer,
              row.category ? translates.for(`machine.types.${row.category}`) : null,
              row.model,
              row.internalNumber,
            ].filter(val => val != null && val !== '').join(' > ')}
          </div> : null}
          {!user ? <div className={`col`} onClick={() => row.userId && goToUserPage ? goToUserPage(row.userId) : navigate(`/communal/statistics/users?user=${row.userId}`)}>
            {row.username || row.email || row.userId}
          </div> : null}
        </>
      }}
      translates={session.translates}
    />
  </div>
}

